import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { Clock, HelpCircle, Undo2, X } from 'lucide-react';
import { progressState } from '@/lib/practice/dotProgress';
import { isOnScreen, isTypingTarget } from './isOnScreen';

import { parseUtcDate } from '@/lib/datetime';

/**
 * `useEndOnEsc` - global Esc handler that ends the active session.
 * INPUT / TEXTAREA / contenteditable are shielded so the user can type
 * in the commit-prompt without quitting by accident.
 *
 * Replaces the keyboard handling previously inside `RunnerTopBar`,
 * which was deleted when the chrome banner was removed.
 */
export function useEndOnEsc(
  onEnd: () => void,
  /** The runner's own node. Only the visible copy responds — see
   *  `isOnScreen`; without this both mounted copies end the session. */
  scopeRef?: RefObject<HTMLElement | null>
) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (scopeRef && !isOnScreen(scopeRef.current)) return;
      if (isTypingTarget(e)) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        onEnd();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onEnd, scopeRef]);
}

/**
 * Floating X end-session button at the top-left corner. Mirrors the
 * HelpButton's floating style for visual rhythm - both are persistent
 * affordances, opposite corners, so the user always knows where
 * "exit" and "help" live.
 */
export function FloatingEndButton({ onEnd }: { onEnd: () => void }) {
  return (
    <button
      type='button'
      onClick={onEnd}
      aria-label='End session'
      title='End session (Esc)'
      className='absolute left-4 top-4 z-20 inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm transition-colors hover:border-gray-300 hover:text-foreground'
    >
      <X className='h-4 w-4' />
    </button>
  );
}

/**
 * Tiny absolute-positioned elapsed-time chip. Pinned to the top-right
 * corner of the runner container (the runner must be
 * `position: relative` for this to anchor correctly).
 */
export function FloatingTimer({ elapsedMs }: { elapsedMs: number }) {
  return (
    <div className='pointer-events-none absolute right-6 top-4 z-10 hidden items-center gap-1.5 text-xs tabular-nums text-muted-foreground sm:flex'>
      <Clock className='h-3 w-3' />
      {formatElapsed(elapsedMs)}
    </div>
  );
}

/**
 * When the session started, once its deck is ready — so the clock measures
 * study time, not the queue / question-generation wait. State rather than a
 * ref so the one change reaches `SessionTimer`; set in an effect so a
 * mid-load unmount can't pin a phantom start time.
 */
export function useSessionStart(ready: boolean): number | null {
  const [startedAt, setStartedAt] = useState<number | null>(null);
  useEffect(() => {
    if (ready) setStartedAt(prev => prev ?? Date.now());
  }, [ready]);
  return startedAt;
}

/**
 * The live elapsed-time chip. Owns its own once-a-second tick so ONLY this
 * leaf re-renders: when the runners held the tick, every second re-rendered
 * the whole card and re-mounted its plots (see `BlockMarkdown`), which
 * collapsed the layout and reset the page's scroll. `Date.now()` per render
 * means a backgrounded tab catches up on resume.
 */
export function SessionTimer({ startedAt }: { startedAt: number | null }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick(t => t + 1), 1000);
    return () => window.clearInterval(id);
  }, []);
  return (
    <FloatingTimer
      elapsedMs={startedAt === null ? 0 : Date.now() - startedAt}
    />
  );
}

/**
 * Top-centered slot for transient momentum cues (ComboBadge,
 * UndoLastRating). Hidden when children are empty.
 */
export function FloatingMomentum({ children }: { children?: ReactNode }) {
  if (!children) return null;
  return (
    <div className='pointer-events-none absolute left-1/2 top-4 z-10 -translate-x-1/2'>
      <div className='pointer-events-auto'>{children}</div>
    </div>
  );
}

/**
 * Bottom-centered progress dots. Sits in a thin real footer so it
 * doesn't collide with rate-row or action-row layouts inside the
 * scrollable content area.
 */
export function ProgressFooter({
  index,
  total,
}: {
  index: number;
  total: number;
}) {
  if (total <= 0) return null;
  return (
    <div className='flex flex-shrink-0 items-center justify-center pb-4 pt-2'>
      <DotProgress index={index} total={total} />
    </div>
  );
}

export interface Shortcut {
  /** Keys rendered as kbd badges, in order. e.g. `['Space']` or
   *  `['1', '2', '3', '4']`. */
  keys: string[];
  /** Plain-English description of what the shortcut does. */
  label: string;
}

/**
 * Floating help button in the bottom-right corner of a surface. Opens a
 * small popover listing the keyboard shortcuts for the current screen.
 *
 * The button is `absolute` - the parent surface must be
 * `position: relative` for it to anchor correctly. The popover is also
 * absolutely positioned and closes on Esc, click-outside, and on
 * clicking the button again.
 */
export function HelpButton({
  shortcuts,
  label = 'Keyboard shortcuts',
}: {
  shortcuts: Shortcut[];
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // Close on Esc + click-outside. We don't intercept Esc when the
  // popover is closed (so it stays available for ending the session).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
      }
    };
    const onClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  return (
    <div ref={ref} className='absolute bottom-4 right-4 z-20'>
      {open && (
        <div
          role='dialog'
          aria-label={label}
          className='absolute bottom-14 right-0 w-72 origin-bottom-right rounded-xl border border-border bg-card p-4 shadow-lg motion-safe:duration-150 motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95'
        >
          <div className='mb-3 flex items-center justify-between'>
            <span className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
              {label}
            </span>
            <button
              type='button'
              onClick={() => setOpen(false)}
              aria-label='Close shortcuts'
              className='inline-flex h-5 w-5 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-muted-foreground'
            >
              <X className='h-3 w-3' />
            </button>
          </div>
          <ul className='space-y-2'>
            {shortcuts.map((s, i) => (
              <li
                key={`${s.label}-${i}`}
                className='flex items-center justify-between gap-3 text-xs'
              >
                <span className='text-foreground'>{s.label}</span>
                <span className='flex flex-shrink-0 items-center gap-1'>
                  {s.keys.map((k, ki) => (
                    <kbd
                      key={`${k}-${ki}`}
                      className='inline-flex h-5 min-w-[20px] items-center justify-center rounded-sm border border-gray-300 bg-muted px-1.5 font-mono text-xs font-medium text-muted-foreground shadow-sm dark:border-gray-600'
                    >
                      {k}
                    </kbd>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <button
        type='button'
        onClick={() => setOpen(o => !o)}
        aria-label={label}
        aria-expanded={open}
        title={label}
        className='group inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-all duration-200 hover:bg-primary/90 hover:shadow-xl'
      >
        <HelpCircle className='h-5 w-5' />
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Per-surface shortcut catalogues. Co-located so the help popover
// stays in sync with what the keyboard handlers actually do.
// ─────────────────────────────────────────────────────────────────

export const SETUP_SHORTCUTS: Shortcut[] = [
  { keys: ['Enter'], label: 'Start Due today session' },
];

export const FLASHCARD_SHORTCUTS: Shortcut[] = [
  { keys: ['Space'], label: 'Reveal answer' },
  { keys: ['1'], label: 'Rate Again' },
  { keys: ['2'], label: 'Rate Hard' },
  { keys: ['3'], label: 'Rate Good' },
  { keys: ['4'], label: 'Rate Easy' },
  { keys: ['S'], label: 'Star concept' },
  { keys: ['U'], label: 'Undo last rating' },
  { keys: ['Esc'], label: 'End session' },
];

export const QUIZ_SHORTCUTS: Shortcut[] = [
  { keys: ['A', 'B', 'C', 'D'], label: 'Pick option' },
  { keys: ['1', '2', '3', '4'], label: 'Pick option (numeric)' },
  { keys: ['Enter'], label: 'Submit / Next' },
  { keys: ['W'], label: "Toggle 'Why?' explanation" },
  { keys: ['S'], label: 'Star concept' },
  { keys: ['Esc'], label: 'End session' },
];

interface DotProgressProps {
  index: number;
  total: number;
}

export function DotProgress({ index, total }: DotProgressProps) {
  // The clamp and the dots/compact switch are a rule, tabled in
  // `lib/practice/dotProgress` — the interesting case is the tick after the
  // final rating, where `index` equals `total`.
  const { safeIndex, position, percent, kind, label } = progressState(
    index,
    total
  );

  if (kind === 'empty') return null;

  if (kind === 'compact') {
    const pct = percent;
    return (
      <div
        className='flex items-center gap-2 text-xs tabular-nums text-muted-foreground'
        aria-label={label}
      >
        <span>
          {position} / {total}
        </span>
        <div className='h-1 w-24 overflow-hidden rounded-full bg-secondary'>
          <div
            className='h-full bg-brand-500 motion-safe:transition-all motion-safe:duration-300 dark:bg-primary'
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className='flex items-center gap-1.5'
      role='progressbar'
      aria-valuenow={position}
      aria-valuemin={1}
      aria-valuemax={total}
      aria-label={label}
    >
      {Array.from({ length: total }).map((_, i) => {
        const tone =
          i < safeIndex
            ? 'bg-gray-300 dark:bg-gray-600'
            : i === safeIndex
              ? 'bg-brand-500 dark:bg-primary'
              : 'bg-transparent border border-gray-300 dark:border-gray-600';
        const size = i === safeIndex ? 'h-2 w-2' : 'h-1.5 w-1.5';
        return (
          <span
            key={i}
            className={`block rounded-full motion-safe:transition-all motion-safe:duration-200 ${size} ${tone}`}
          />
        );
      })}
    </div>
  );
}

interface ConceptContextStripProps {
  domain?: string;
  level: string | null;
  lastReviewedAt: string | null;
  retention: number;
}

/**
 * Slim inline metadata strip rendered above the question card. Replaces
 * the older (i)-toggle + drawer pattern - the icon felt orphaned in the
 * top bar, and the drawer was a click-cost on info the user often wants
 * at a glance. Dot-separated, muted, generous breathing room above and
 * below so it doesn't compete with the question hero.
 */
export function ConceptContextStrip({
  domain,
  level,
  lastReviewedAt,
  retention,
}: ConceptContextStripProps) {
  const reviewed = lastReviewedAt
    ? formatRelativeDays(lastReviewedAt)
    : 'Never reviewed';
  const pct = Math.round(retention * 100);
  // Threshold-based tone - the only place a non-indigo accent stays,
  // because retention quality is a status signal the eye should catch
  // at a glance.
  // -700 rather than -600: measured on the runner's own card surface, the
  // rose variant came to 4.19:1 at 11px and missed AA. The whole -600 family
  // sits just under the line at this size on these tinted surfaces, which is
  // the same finding the history row's percentage fallback produced. Amber
  // goes one step further: -700 measured 4.48:1 on the revealed card's brand
  // tint (#eef2fe) at 11px — reached by `BuildsAFlashcardCombo`, whose fifth
  // card sits in the 60–79% band.
  const retentionTone =
    pct >= 80
      ? 'text-emerald-700 dark:text-emerald-400'
      : pct >= 60
        ? 'text-amber-800 dark:text-amber-400'
        : 'text-rose-700 dark:text-rose-400';

  // Build the parts list so we can dot-separate without trailing
  // separators when an optional field (domain) is missing.
  const parts: Array<{ key: string; node: ReactNode }> = [];
  if (domain) parts.push({ key: 'domain', node: <span>{domain}</span> });
  if (level) parts.push({ key: 'level', node: <span>{level}</span> });
  parts.push({ key: 'reviewed', node: <span>{reviewed}</span> });
  parts.push({
    key: 'retention',
    node: <span className={retentionTone}>Retention {pct}%</span>,
  });

  return (
    <div className='flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground'>
      {parts.map((p, i) => (
        <span key={p.key} className='inline-flex items-center gap-1.5'>
          {p.node}
          {i < parts.length - 1 && <span className='text-gray-300'>·</span>}
        </span>
      ))}
    </div>
  );
}

interface UndoLastRatingProps {
  /** Click to revert the last rating. Called once and then the pill
   *  hides itself. */
  onUndo: () => void;
  /** Bumped by the parent every time a new rating fires - resets the
   *  3-second visibility timer. Pass `null` (or a key that never
   *  changes) to keep the pill hidden. */
  rateKey: number | null;
}

const UNDO_GRACE_MS = 5000;

export function UndoLastRating({ onUndo, rateKey }: UndoLastRatingProps) {
  const pillRef = useRef<HTMLButtonElement | null>(null);
  const [visible, setVisible] = useState(false);
  // Brief amber-ring on entry so a fresh undo opportunity catches the
  // eye without being loud for the full 5s grace window.
  const [fresh, setFresh] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const freshTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (rateKey == null) {
      setVisible(false);
      setFresh(false);
      return;
    }
    setVisible(true);
    setFresh(true);
    window.clearTimeout(timer.current);
    window.clearTimeout(freshTimer.current);
    timer.current = window.setTimeout(() => setVisible(false), UNDO_GRACE_MS);
    freshTimer.current = window.setTimeout(() => setFresh(false), 1000);
    return () => {
      window.clearTimeout(timer.current);
      window.clearTimeout(freshTimer.current);
    };
  }, [rateKey]);

  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      // Visible copy only: two mounted pills meant `U` rewound TWO cards.
      if (!isOnScreen(pillRef.current)) return;
      if (isTypingTarget(e)) return;
      if (e.key === 'u' || e.key === 'U') {
        e.preventDefault();
        onUndo();
        setVisible(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, onUndo]);

  if (!visible) return null;

  const freshRing = fresh
    ? 'ring-2 ring-amber-300/70 dark:ring-amber-500/40'
    : 'ring-0 ring-transparent';

  return (
    <button
      ref={pillRef}
      type='button'
      onClick={() => {
        onUndo();
        setVisible(false);
      }}
      className={`flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-0.5 text-xs font-medium text-muted-foreground shadow-sm hover:border-gray-300 hover:text-foreground motion-safe:transition-all motion-safe:duration-200 motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 ${freshRing}`}
    >
      <Undo2 className='h-3 w-3' />
      Undo
      <kbd className='ml-0.5 hidden font-mono text-xs text-muted-foreground sm:inline'>
        U
      </kbd>
    </button>
  );
}

function formatElapsed(ms: number): string {
  const total = Math.floor(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function formatRelativeDays(iso: string): string {
  const then = parseUtcDate(iso).getTime();
  if (Number.isNaN(then)) return 'Recently';
  const diffMs = Date.now() - then;
  const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return `${months} mo ago`;
}
